import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { pmGet, pmPost } from '../api';
import { pd, fmtY, ensureBiz, addBizInclusive, nextBiz, businessDays } from './businessDays';
import { SelectField, PROMPT } from '@platform/ui-kit';
import { TimelineGantt } from './TimelineGantt';
import { PHASE_COLORS, MVP_PALETTE } from './timelineColors';
import './TimelineFinalization.css';
import { HelpPanel, type HelpContent } from '../../portfolio_definition/HelpPanel';
import { withFinalizedRule, FINALIZED_RULE_EXCEPTION } from '../finalizedRuleHelp';

/**
 * Timeline Finalization.
 *
 * CLONED from `ai_readiness_template/views/roadmap-execution/finalization.ejs`, read end to end
 * first. Tabs, card layout, badge wording, button labels, the program-window note and the empty
 * state are the source's; every CSS value is in the stylesheet beside this file.
 *
 * ── WHAT IT DOES ───────────────────────────────────────────────────────────────────────
 *
 * Set ONE project-level start date per delivery project. Every opportunity under it is
 * re-laid from that date and the timeline is locked.
 *
 * ── THE RECOMPUTE RULE, WHICH IS THE WHOLE SCREEN ──────────────────────────────────────
 *
 * `recomputeProject` walks each opportunity's five phases in order and re-lays them from the
 * new start, PRESERVING EACH PHASE'S WORKING-DAY LENGTH:
 *
 *     working days of the phase as it stands  →  same number of working days from the new start
 *     each phase begins on the next working day after the previous one ends
 *
 * So moving a project's start date shifts the whole shape without distorting it, and weekends
 * and holidays are skipped rather than absorbed. The arithmetic is in `businessDays.ts`, cloned
 * from the same helpers the source's two roadmap views both carry.
 *
 * ── TWO BUTTONS, ONE ENDPOINT ──────────────────────────────────────────────────────────
 *
 *   Save      `finalize: false` — persist the edited dates and leave the project in Draft
 *   Finalize  `finalize: true`  — persist AND lock
 *
 * An Agile project has no Waterfall phases to re-lay, so it shows its MVP waves and offers
 * only the start date and Finalize — the source's own branch.
 */

interface Phase {
  name: string;
  label: string;
  start_date: string;
  end_date: string;
}
interface Opportunity {
  opportunity_id: string;
  standalone: boolean;
  project_id: string | null;
  description: string | null;
  planned_start_date: string | null;
  planned_end_date: string | null;
  methodology: string | null;
  phases: Phase[];
  /*
   * The generated MVP/sprint schedule, present only on an Agile initiative. The endpoint has
   * always returned it; this type simply never declared it, so the Gantt could not colour an
   * Agile initiative by wave — every sprint fell through to the phase branch.
   */
  agile?: { mvps?: { sprints?: { start: string | null; end: string | null }[] }[] } | null;
}
interface Mvp {
  id: number;
  seq: number;
  name: string | null;
  theme: string | null;
  start_date: string | null;
  end_date: string | null;
  sprint_count: number;
}
interface Project {
  delivery_project_id: string;
  description: string | null;
  project_start_date: string | null;
  finalized: boolean;
  methodology: string | null;
  window_start?: string | null;
  window_end?: string | null;
  opportunities: Opportunity[];
  mvps?: Mvp[];
}

/** The earliest phase date anywhere in the project — the source's `earliestStart`. */
function earliestStart(proj: Project): string {
  const all = proj.opportunities
    .flatMap((o) => o.phases.map((p) => p.start_date))
    .filter(Boolean)
    .sort();
  return all.length ? all[0]! : '';
}

/**
 * Re-lay every phase of every opportunity from `startStr`, preserving working-day lengths.
 *
 * Returns a NEW project object rather than mutating — the source mutates its global and
 * re-renders, which React cannot see. The rule itself is unchanged.
 */
function recomputeProject(proj: Project, startStr: string): Project | null {
  const start = pd(startStr);
  if (!start) return null;
  return {
    ...proj,
    opportunities: proj.opportunities.map((op) => {
      let cur = ensureBiz(start);
      let firstStart: string | null = null;
      let lastEnd: string | null = null;
      const phases = op.phases.map((p) => {
        /* The phase's CURRENT working-day length is what carries over. */
        const wd = businessDays(p.start_date, p.end_date);
        const ps = new Date(cur);
        const pe = addBizInclusive(ps, wd);
        const next = { ...p, start_date: fmtY(ps), end_date: fmtY(pe) };
        if (!firstStart) firstStart = next.start_date;
        lastEnd = next.end_date;
        cur = nextBiz(pe);
        return next;
      });
      return { ...op, phases, planned_start_date: firstStart, planned_end_date: lastEnd };
    }),
  };
}

/**
 * "How this works" — cloned from `ai_readiness_template/views/roadmap-execution/finalization.ejs`,
 * its `finalizationHelp` object. Copy, colours and section order are the source's;
 * only the link hrefs are rewritten to this platform's routes, and the entities in
 * fields HelpPanel renders as TEXT (title, section label, term, link label) are
 * decoded, because only the HTML fields go through `dangerouslySetInnerHTML`.
 */
const FINALIZATION_HELP: HelpContent = {
    title: 'Project Timeline Finalization',
    icon: '📅',
    purpose: "Set and adjust the <strong>delivery timeline</strong> for each project. Every opportunity's <strong>phase dates are editable</strong> and bounded by the project's overall <strong>window</strong>. Auto&#8209;fill from a single start date, then fine&#8209;tune any date. <strong>Save</strong> keeps a draft; <strong>Finalize</strong> commits the timeline.",
    doHere: [
      "Switch between <strong>To Finalize</strong> (drafts still to commit) and <strong>Finalized</strong> (committed timelines), then use the <strong>View project</strong> pull-down to focus on one project at a time &mdash; or pick <strong>All projects</strong> to see the whole tab.",
      "<strong>Expand an opportunity</strong> to edit its five phase dates directly &mdash; the opportunity's Start/End and the Gantt update live.",
      "An opportunity whose methodology is <strong>None</strong> has <strong>no phases</strong>: expanding it shows only its <strong>Start</strong> and <strong>End</strong>, which are its whole timeline. A project finalizes normally with one, several or all of its opportunities set to None &mdash; only the window is validated.",
      "Or use <strong>Auto&#8209;fill</strong> from a start date to populate every phase over working days, then tweak any individual date.",
      "All dates must stay within the <strong>Project window</strong> shown on the card &mdash; out&#8209;of&#8209;range dates are highlighted.",
      "<strong>Save</strong> keeps your edits as a draft; <strong>Finalize</strong> validates and commits. Adding a new opportunity to a project re&#8209;opens it (Draft) so its deadline can be finalized."
    ],
    fits: 'Project Creation &rarr; <strong>Project Timeline Finalization</strong> &rarr; Stream Definition &rarr; Work Item Definition',
    firstTime: "Expand an opportunity, set its phase dates (or click <strong>Auto&#8209;fill</strong>) &mdash; or just its Start and End if its methodology is <strong>None</strong> &mdash; then <strong>Save</strong> or <strong>Finalize</strong>.",
    sections: [
      {
        label: 'The five delivery phases',
        type: 'defs',
        items: [
          { term: 'Assessment', color: '#42a5f5', def: "Assessment &amp; Planning &mdash; scoping, mobilization and discovery." },
          { term: 'Design', color: '#ab47bc', def: "Design &amp; Architecture &mdash; solution design or blueprint." },
          { term: 'Development', color: '#66bb6a', def: "Build &mdash; configuration, development and integration." },
          { term: 'Testing', color: '#ffa726', def: "Testing &amp; Validation &mdash; quality assurance and acceptance." },
          { term: 'Go-Live', color: '#ef5350', def: "Go-Live &amp; Optimization &mdash; cutover, hypercare and stabilization." }
        ],
        note: "Durations are computed over <strong>working days</strong> (weekends and US holidays skipped) from the start date you choose. Opportunities set to <strong>Agile</strong> are scheduled as MVP waves and sprints instead, and those set to <strong>None</strong> have no phases at all &mdash; just one flat window."
      }
    ],
    links: [
      { label: 'Full guide: how the platform works', href: '/project_management/user-guide#work-items' },
      { label: 'Next: Stream Definition →', href: '/project_management/program-management/scope-ownership/stream-definition' }
    ]
  };

export function TimelineFinalization() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<'todo' | 'done'>('todo');
  const [openOpps, setOpenOpps] = useState<Set<string>>(new Set());
  /** The date typed in each card's start box, before Auto-fill or Save is pressed. */
  const [startDrafts, setStartDrafts] = useState<Record<string, string>>({});
  /**
   * Table or Gantt for ONE EXPANDED OPPORTUNITY — the source's `oppViewFor`, and the only
   * view switch on this screen. The PROJECT chart is not switchable: it is always shown,
   * anchored above the opportunity table.
   */
  const [oppView, setOppView] = useState<Record<string, 'table' | 'gantt'>>({});
  /** Zoom per project card. 'fit' scales the whole span into the width. */
  const [zoom, setZoom] = useState<Record<string, number | 'fit'>>({});
  /**
   * The project filter — the source's `selectedProjects`, which this screen was ported
   * without.
   *
   * Multi-select by CTRL-CLICK, never checkboxes — a plain click picks one project, Ctrl-click
   * (Cmd on a Mac) adds or removes. The field therefore looks exactly like every other Project
   * field on the platform, which is the point. Nothing picked means every project.
   *
   * ONE DELIBERATE DIFFERENCE, and it is the safe direction: the source shows NOTHING until a
   * project is picked ("Nothing is shown until the user picks project(s)"). This screen has
   * always shown every project, so an empty selection keeps doing that — adding a filter must
   * not make cards that are on screen today disappear. Narrowing to the source's rule is a
   * one-line change if that is wanted.
   */
  const [pickedProjects, setPickedProjects] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await pmGet<{ success: boolean; projects?: Project[]; error?: string }>(
        '/api/definition/finalization/projects',
      );
      if (!d.success) throw new Error(d.error ?? 'Could not load projects');
      setProjects(d.projects ?? []);
      setStartDrafts({});
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const todo = useMemo(() => projects.filter((p) => !p.finalized), [projects]);
  const done = useMemo(() => projects.filter((p) => p.finalized), [projects]);
  const inTab = tab === 'todo' ? todo : done;
  /* Selections are scoped to the tab, as the source does — an id that is not in the current
     tab is ignored rather than silently emptying the list. */
  const tabIds = useMemo(() => new Set(inTab.map((p) => p.delivery_project_id)), [inTab]);
  /* A project chosen on one tab is ignored on the other rather than emptying it — the source
     prunes ids that are not in the current tab, and this is the same rule for one id. */
  const picked = useMemo(
    () => pickedProjects.filter((id) => tabIds.has(id)),
    [pickedProjects, tabIds],
  );
  const shown = picked.length
    ? inTab.filter((p) => picked.includes(p.delivery_project_id))
    : inTab;

  const startOf = (p: Project) =>
    startDrafts[p.delivery_project_id] ?? p.project_start_date ?? earliestStart(p);

  /** Auto-fill — re-lay the phases locally so they can be reviewed BEFORE saving. */
  const autofill = (p: Project) => {
    const start = startOf(p);
    if (!pd(start)) {
      setError('Enter a start date to auto-fill from.');
      return;
    }
    const next = recomputeProject(p, start);
    if (!next) return;
    setProjects((prev) =>
      prev.map((x) =>
        x.delivery_project_id === p.delivery_project_id
          ? { ...next, project_start_date: start }
          : x,
      ),
    );
    setNotice(
      `Phases re-laid from ${start}, keeping each phase's working-day length. Review, then Save or Finalize.`,
    );
  };

  const apply = async (p: Project, finalize: boolean) => {
    setError(null);
    setNotice(null);
    try {
      const d = await pmPost<{ success: boolean; error?: string }>(
        '/api/definition/finalization/apply',
        {
          delivery_project_id: p.delivery_project_id,
          start_date: startOf(p) || null,
          finalize,
          opportunities: p.opportunities.map((o) => ({
            opportunity_id: o.opportunity_id,
            standalone: o.standalone,
            planned_start_date: o.planned_start_date,
            planned_end_date: o.planned_end_date,
            phases: o.phases,
          })),
        },
      );
      if (!d.success) throw new Error(d.error ?? 'unknown');
      setNotice(
        finalize
          ? `${p.delivery_project_id} finalized — its phase windows are locked for execution.`
          : `${p.delivery_project_id} saved. It stays in Draft until you finalize it.`,
      );
      await load();
    } catch (e) {
      setError(`Could not ${finalize ? 'finalize' : 'save'}: ${(e as Error).message}`);
    }
  };

  /** Editing one phase date in place, without re-laying anything else. */
  const setPhaseDate = (
    p: Project,
    oid: string,
    phaseName: string,
    which: 'start_date' | 'end_date',
    value: string,
  ) => {
    setProjects((prev) =>
      prev.map((proj) => {
        if (proj.delivery_project_id !== p.delivery_project_id) return proj;
        return {
          ...proj,
          opportunities: proj.opportunities.map((o) => {
            if (o.opportunity_id !== oid) return o;
            const phases = o.phases.map((ph) =>
              ph.name === phaseName ? { ...ph, [which]: value } : ph,
            );
            /* The opportunity's span follows its phases — otherwise the row header and the
               phases it contains would disagree. */
            const starts = phases.map((x) => x.start_date).filter(Boolean).sort();
            const ends = phases.map((x) => x.end_date).filter(Boolean).sort();
            return {
              ...o,
              phases,
              planned_start_date: starts[0] ?? o.planned_start_date,
              planned_end_date: ends.length ? ends[ends.length - 1]! : o.planned_end_date,
            };
          }),
        };
      }),
    );
  };

  /**
   * Start / End for a phase-less ("None") initiative — the source's `onFlatDate`.
   *
   * Its window IS its whole plan, so it is edited directly rather than through phases.
   */
  const setFlatDate = (
    p: Project,
    oid: string,
    which: 'planned_start_date' | 'planned_end_date',
    value: string,
  ) =>
    setProjects((prev) =>
      prev.map((proj) =>
        proj.delivery_project_id !== p.delivery_project_id
          ? proj
          : {
              ...proj,
              opportunities: proj.opportunities.map((o) =>
                o.opportunity_id === oid ? { ...o, [which]: value } : o,
              ),
            },
      ),
    );

  const toggleOpp = (oid: string) =>
    setOpenOpps((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid);
      else next.add(oid);
      return next;
    });

  return (
    <div className="tf-page">
      <HelpPanel help={withFinalizedRule(FINALIZATION_HELP, FINALIZED_RULE_EXCEPTION)} />
      <div className="tf-head">
        <h1 className="page-header__title">Timeline Finalization</h1>
        <p className="tf-sub">
          Set one <strong>project-level start date</strong> per work item project. All its
          opportunities are aligned to that date and the roadmap timeline is finalized.
        </p>

        <div className="tf-tabs">
          <button
            type="button"
            className={`tf-tab${tab === 'todo' ? ' on' : ''}`}
            onClick={() => setTab('todo')}
          >
            To Finalize <span className="tf-cnt">{todo.length ? `(${todo.length})` : ''}</span>
          </button>
          <button
            type="button"
            className={`tf-tab${tab === 'done' ? ' on' : ''}`}
            onClick={() => setTab('done')}
          >
            Finalized <span className="tf-cnt">{done.length ? `(${done.length})` : ''}</span>
          </button>

          {/* The project filter sits in the tab row itself, immediately after Finalized.
              Refresh keeps its `margin-left:auto`, so it stays pinned to the right. */}
          {inTab.length ? (
            <SelectField
              className="tf-filter"
              /* `style` is spread LAST over SelectField's own inline styles, so this is the
                 only thing that can beat its `alignItems:'flex-start'`. A class rule cannot —
                 which is why the label sat above the tabs instead of level with them.
                 flex-start is right when a chosen value wraps to several lines; in a
                 single-line toolbar row, centre is. */
              style={{ alignItems: 'center' }}
              label="Project"
              width="lg"
              multiple
              allLabel="All Projects"
              placeholder={PROMPT.project}
              value={picked}
              onChange={setPickedProjects}
              options={inTab.map((p) => ({
                value: p.delivery_project_id,
                id: p.delivery_project_id,
                description: p.description,
              }))}
            />
          ) : null}

          <button type="button" className="tf-refresh" title="Refresh" onClick={() => void load()}>
            ↻ Refresh
          </button>
        </div>

      </div>

      <div className="tf-body">
        {error ? <div className="tf-err">{error}</div> : null}
        {notice ? <div className="tf-note">{notice}</div> : null}

        {loading ? (
          <div className="tf-empty">Loading…</div>
        ) : !shown.length ? (
          <div className="tf-empty">
            <div className="tf-empty-icon">🗂️</div>
            <h3>No Projects Yet</h3>
            <p>
              Create work item projects on the <strong>Project Creation</strong> screen first,
              then finalize their timelines here.
            </p>
          </div>
        ) : (
          shown.map((p) => {
            const isAgile = p.methodology === 'Agile';
            const mvps = p.mvps ?? [];
            return (
              <div className="tf-card" key={p.delivery_project_id}>
                <div className="tf-card-hd">
                  <div className="tf-card-id">
                    <span className="tf-pid">📦 {p.delivery_project_id}</span>
                    <span className={`tf-badge ${p.finalized ? 'done' : 'draft'}`}>
                      {p.finalized ? '✓ Finalized' : 'Draft'}
                    </span>
                    <div className="tf-desc">{p.description ?? ''}</div>
                    <div className="tf-meta">
                      {isAgile
                        ? `${mvps.length} MVP wave${mvps.length === 1 ? '' : 's'} · Agile`
                        : `${p.opportunities.length} opportunit${p.opportunities.length === 1 ? 'y' : 'ies'} · Waterfall`}
                    </div>
                    {p.window_start || p.window_end ? (
                      <div className="tf-window">
                        Project window: <strong>{p.window_start ?? '—'}</strong> →{' '}
                        <strong>{p.window_end ?? '—'}</strong> — all dates must fall within this
                        range.
                      </div>
                    ) : null}
                  </div>

                  <div className="tf-actions">
                    <div>
                      <label className="tf-lbl">
                        {isAgile ? 'Project Start Date' : 'Auto-fill from'}
                      </label>
                      <input
                        type="date"
                        className={`tf-date${isAgile ? ' primary' : ''}`}
                        value={startOf(p)}
                        onChange={(e) =>
                          setStartDrafts((prev) => ({
                            ...prev,
                            [p.delivery_project_id]: e.target.value,
                          }))
                        }
                      />
                    </div>
                    {/* An Agile project has no Waterfall phases to re-lay, so it gets neither
                        Auto-fill nor Save — the source's own branch. */}
                    {!isAgile ? (
                      <>
                        <button
                          type="button"
                          className="tf-btn"
                          title="Fill every phase date from this start over working days. You can then edit any individual date."
                          onClick={() => autofill(p)}
                        >
                          Auto-fill
                        </button>
                        <span className="tf-vsep" />
                        <button
                          type="button"
                          className="tf-btn save"
                          onClick={() => void apply(p, false)}
                        >
                          Save
                        </button>
                      </>
                    ) : null}
                    <button
                      type="button"
                      className="tf-btn finalize"
                      onClick={() => void apply(p, true)}
                    >
                      {p.finalized ? 'Re-finalize' : 'Finalize'}
                    </button>
                  </div>
                </div>

                <div className="tf-card-body">
                  {isAgile ? (
                    <>
                      <div className="tf-section-lbl">MVP Timeline</div>
                      {mvps.length ? (
                        <div className="tf-mvps">
                          {mvps.map((m, i) => (
                            <div className="tf-mvp" key={m.id}>
                              <span
                                className="tf-mvp-dot"
                                style={{ background: MVP_PALETTE[i % MVP_PALETTE.length] }}
                              />
                              <span className="tf-mvp-name">
                                {m.name || `MVP ${m.seq}`}
                                {m.theme ? <span className="tf-mvp-theme"> · {m.theme}</span> : null}
                              </span>
                              <span className="tf-mvp-dates">
                                {m.start_date ?? '—'} → {m.end_date ?? '—'}
                              </span>
                              <span className="tf-mvp-sprints">
                                {m.sprint_count} sprint{m.sprint_count === 1 ? '' : 's'}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="tf-none">
                          No MVP waves defined yet for this project.
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      {/*
                        THE PROJECT GANTT IS ALWAYS SHOWN, ANCHORED ABOVE THE TABLE.
                        Both together — the source calls `renderProjectGantt` and
                        `renderOppTable` on every render and never makes them exclusive.
                        A project-level Table/Gantt switch was briefly added here and it was
                        wrong: choosing Table hid the chart, which is the one thing on this
                        screen that shows how the initiatives overlap.
                        The ONLY view switch is the per-opportunity one further down.
                      */}
                      <div className="tf-section-bar">
                        <span className="tf-section-lbl">
                          Timeline — expand an opportunity to edit its phase dates
                        </span>
                      </div>

                      <TimelineGantt
                        opportunities={p.opportunities}
                        zoom={zoom[p.delivery_project_id] ?? 'fit'}
                        onZoom={(z) =>
                          setZoom((prev) => ({ ...prev, [p.delivery_project_id]: z }))
                        }
                      />

                      <table className="tf-tbl">
                        <thead>
                          <tr>
                            <th className="left" style={{ width: 170 }}>
                              Opportunity
                            </th>
                            <th className="left">Description</th>
                            <th style={{ width: 110 }}>Start</th>
                            <th style={{ width: 110 }}>End</th>
                          </tr>
                        </thead>
                        <tbody>
                          {p.opportunities.map((o) => {
                            const open = openOpps.has(o.opportunity_id);
                            const oView = oppView[o.opportunity_id] ?? 'gantt';
                            /* "None" and "not chosen yet" are both phase-less here — the same
                               rule the server applies in `phaseRules.isFlat`. */
                            const oMeth = (o.methodology ?? '').trim();
                            const oFlat = oMeth === '' || oMeth === 'None';
                            return (
                              <Fragment key={o.opportunity_id}>
                                <tr className="tf-opprow">
                                  <td className="left">
                                    <span
                                      className="tf-oppid"
                                      onClick={() => toggleOpp(o.opportunity_id)}
                                    >
                                      <span className="tf-ocaret">{open ? '▼' : '▶'}</span>
                                      {o.opportunity_id}
                                    </span>
                                  </td>
                                  <td className="left tf-oppdesc">{o.description ?? ''}</td>
                                  <td>{o.planned_start_date || '-'}</td>
                                  <td>{o.planned_end_date || '-'}</td>
                                </tr>
                                {open ? (
                                  <tr>
                                    <td colSpan={4} className="tf-detail">
                                      {/* Table / Gantt for this one initiative — the source's
                                          `setOppView`. */}
                                      <div className="tf-oppview">
                                        <span className="tf-oppview-lbl">View:</span>
                                        <span className="tf-viewsw">
                                        <button
                                          type="button"
                                          className={oView === 'table' ? 'on' : ''}
                                          onClick={() =>
                                            setOppView((v) => ({
                                              ...v,
                                              [o.opportunity_id]: 'table',
                                            }))
                                          }
                                        >
                                          Table
                                        </button>
                                        <button
                                          type="button"
                                          className={oView === 'gantt' ? 'on' : ''}
                                          onClick={() =>
                                            setOppView((v) => ({
                                              ...v,
                                              [o.opportunity_id]: 'gantt',
                                            }))
                                          }
                                        >
                                          Gantt
                                        </button>
                                        </span>
                                      </div>

                                      {oFlat ? (
                                        /*
                                         * A "None" initiative HAS NO PHASES, so the phase table
                                         * rendered empty and the row looked broken. Its window
                                         * is the whole plan — the source shows exactly Start
                                         * and End, and so does this.
                                         */
                                        <div className="tf-flat">
                                          <span>
                                            <b>Start</b>
                                            <input
                                              type="date"
                                              className="tf-date"
                                              value={o.planned_start_date ?? ''}
                                              min={p.window_start ?? undefined}
                                              max={p.window_end ?? undefined}
                                              onChange={(e) =>
                                                setFlatDate(
                                                  p,
                                                  o.opportunity_id,
                                                  'planned_start_date',
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </span>
                                          <span>
                                            <b>End</b>
                                            <input
                                              type="date"
                                              className="tf-date"
                                              value={o.planned_end_date ?? ''}
                                              min={p.window_start ?? undefined}
                                              max={p.window_end ?? undefined}
                                              onChange={(e) =>
                                                setFlatDate(
                                                  p,
                                                  o.opportunity_id,
                                                  'planned_end_date',
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </span>
                                          <span className="tf-flat-note">
                                            Methodology None — no phases, one flat window
                                          </span>
                                        </div>
                                      ) : null}

                                      {oView === 'gantt' ? (
                                        <TimelineGantt
                                          opportunities={[o]}
                                          single
                                          zoom={zoom[p.delivery_project_id] ?? 'fit'}
                                          onZoom={(z) =>
                                            setZoom((prev) => ({
                                              ...prev,
                                              [p.delivery_project_id]: z,
                                            }))
                                          }
                                        />
                                      ) : oFlat ? null : (
                                      <table className="tf-phases">
                                        <thead>
                                          <tr>
                                            <th className="left">Phase</th>
                                            <th>Start</th>
                                            <th>End</th>
                                            <th>Working days</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {o.phases.map((ph) => (
                                            <tr key={ph.name}>
                                              <td className="left">
                                                <span
                                                  className="tf-pdot"
                                                  style={{
                                                    background: PHASE_COLORS[ph.name] ?? '#bbb',
                                                  }}
                                                />
                                                {ph.label}
                                              </td>
                                              <td>
                                                <input
                                                  type="date"
                                                  className="tf-date"
                                                  value={ph.start_date}
                                                  min={p.window_start ?? undefined}
                                                  max={p.window_end ?? undefined}
                                                  onChange={(e) =>
                                                    setPhaseDate(
                                                      p,
                                                      o.opportunity_id,
                                                      ph.name,
                                                      'start_date',
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td>
                                                <input
                                                  type="date"
                                                  className="tf-date"
                                                  value={ph.end_date}
                                                  min={p.window_start ?? undefined}
                                                  max={p.window_end ?? undefined}
                                                  onChange={(e) =>
                                                    setPhaseDate(
                                                      p,
                                                      o.opportunity_id,
                                                      ph.name,
                                                      'end_date',
                                                      e.target.value,
                                                    )
                                                  }
                                                />
                                              </td>
                                              <td className="tf-wd">
                                                {ph.start_date && ph.end_date
                                                  ? businessDays(ph.start_date, ph.end_date)
                                                  : '—'}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                      )}
                                    </td>
                                  </tr>
                                ) : null}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* PHASE_COLORS and MVP_PALETTE now live in `timelineColors.ts`, because the Gantt beside this
   screen draws with exactly the same values. The hexes are unchanged. */
